import { useLocation } from "@solidjs/router";
import { subscribeToGlobalEvent } from "@storytell/events";
import clsx from "clsx";
import { type ParentComponent, Show, createMemo, onMount } from "solid-js";
import { ArtifactPanel } from "~/components/chat/ArtifactPanel";
import { ResizableLayout } from "~/components/chat/ResizableLayout";
import { BreakpointBug } from "~/components/debug/BreakpointBug";
import { KnowledgePane } from "~/components/knowledge";
import { useArtifactStore } from "~/stores/ArtifactStore";
import {
  AppUIContextProvider,
  ExtendedPaneMode,
  NavigationPaneMode,
  isExtendedPaneVisible,
  useAppUI,
} from "./AppUIContext";
import { ExtendedPane } from "./ExtendedPane";
import { NavigationPane } from "./NavigationPane";

export const MinimalLayout: ParentComponent = (props) => {
  const { state, actions } = useAppUI();
  const [artifactStore, artifactActions] = useArtifactStore();

  const location = useLocation();
  const showNavigationPane = () => !location.pathname.startsWith("/auth");

  // Calculate main content area positioning for navigation pane
  // Extended pane now overlays content without displacing it
  const contentStyles = createMemo(() => {
    let marginLeft = 0;

    // Navigation pane: always push content
    // COLLAPSED: 48px for thin strip, EXPANDED: full panel width
    const COLLAPSED_WIDTH = 48; // w-12 = 48px
    if (showNavigationPane()) {
      marginLeft =
        state.navigationPane.mode === NavigationPaneMode.EXPANDED ? state.navigationPane.width : COLLAPSED_WIDTH;
    }

    // Extended pane overlays content (no margin-right)

    return {
      "margin-left": `${marginLeft}px`,
    };
  });

  // Add keyboard shortcut handler for debug mode
  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for CMD + SHIFT + CTRL + P (debug mode)
      if (e.metaKey && e.shiftKey && e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        actions.toggleDebugMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    const unsubscribeClosePanel = subscribeToGlobalEvent("close-artifact-panel", () => {
      artifactActions.hidePanel();

      // Also close ExtendedPane if it's showing an artifact
      if (isExtendedPaneVisible(state.extendedPane) && state.extendedPane.activeArtifact) {
        actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
      }
    });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      unsubscribeClosePanel();
    };
  });

  return (
    <div class="min-h-screen relative" data-testid="minimal-layout">
      {/* Navigation Pane */}
      <Show when={showNavigationPane()}>
        <NavigationPane />
      </Show>

      {/* Main Content Area with ResizableLayout for artifact panel */}
      <div
        class="min-h-screen transition-[margin] duration-300 ease-in-out"
        style={contentStyles()}
        data-testid="main-content-area"
      >
        <ResizableLayout
          class="h-screen"
          leftContent={props.children}
          rightContent={
            <Show when={artifactStore().isPanelVisible && !isExtendedPaneVisible(state.extendedPane)}>
              <ArtifactPanel
                artifacts={artifactStore().artifacts}
                activeArtifactId={artifactStore().activeArtifactId}
                onArtifactSelect={artifactActions.setActiveArtifact}
                onArtifactClose={artifactActions.removeArtifact}
              />
            </Show>
          }
          showRightPanel={artifactStore().isPanelVisible && !isExtendedPaneVisible(state.extendedPane)}
          defaultRightWidth={artifactStore().panelWidth}
          minLeftWidth={480}
          minRightWidth={320}
          onResize={(_leftWidth, rightWidth) => {
            artifactActions.setPanelWidth(rightWidth);
          }}
        />
      </div>

      {/* Extended Pane */}
      <ExtendedPane />

      {/* Debug Tools */}
      <Show when={state.isDebugMode}>
        <BreakpointBug />
      </Show>

      <KnowledgePane />
    </div>
  );
};

// Export the provider wrapper for convenience
export const MinimalLayoutProvider: ParentComponent = (props) => {
  return (
    <AppUIContextProvider>
      <MinimalLayout>{props.children}</MinimalLayout>
    </AppUIContextProvider>
  );
};

// Re-export the context provider for direct use
export { AppUIContextProvider } from "./AppUIContext";
